import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";

import {
  clearOtp,
  clearPendingBootstrap,
  normalizeRole,
  normalizeTenantId,
  setPendingBootstrap,
  setOtpSentTo,
  setUser,
  type DataScopeType,
} from "../../auth/authSlice";
import { setPermissions } from "../../auth/permissionsSlice";
import { QueryKeys } from "../../config/QueryKeys";
import { clearStoredAcademicYearSelection } from "../../features/academics/academicYearContext/academicYearSelectionStorage";
import * as authService from "../../services/authService";
import { normalizePermissionKeys } from "../../utils/permissionKeyNormalization";

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function persistAuthenticatedSession(
  dispatch: ReturnType<typeof useDispatch>,
  authResult: authService.AuthenticationResult,
  activeDomainEntityId?: string | null,
) {
  const tokenExpiry = authService.getTokenExpiration(authResult.expiresIn).toString();

  localStorage.setItem("accessToken", authResult.accessToken);
  localStorage.setItem("refreshToken", authResult.refreshToken);
  localStorage.setItem("userId", authResult.userId);
  localStorage.setItem("tokenExpiry", tokenExpiry);

  const effectivePermissionKeys = Array.isArray(authResult.effectivePermissionKeys)
    ? authResult.effectivePermissionKeys
    : [];
  const normalizedPermissionKeys = normalizePermissionKeys(effectivePermissionKeys);

  const session = {
    user: {
      id: authResult.userId,
      email: authResult.email,
      name: authResult.userName,
      role: normalizeRole(authResult.roles),
      key:
        authResult.activeContext?.toLowerCase?.() ??
        authResult.roles?.[0]?.toLowerCase?.(),
      schoolId: authResult.tenantId,
      tenantId: normalizeTenantId(authResult.tenantId),
      tenantName: authResult.tenantName,
      accessToken: authResult.accessToken,
      refreshToken: authResult.refreshToken,
      dataScope: (authResult.dataScope ?? "Unrestricted") as DataScopeType,
      domainIdentities: authResult.domainIdentities,
      domainType:
        authResult.domainIdentities?.find(
          (identity) =>
            identity.tenantId === authResult.tenantId &&
            identity.domainEntityId === activeDomainEntityId,
        )?.domainType ??
        authResult.domainIdentities?.find(
          (identity) => identity.tenantId === authResult.tenantId,
        )?.domainType,
      activeContext: authResult.activeContext,
      scope: authResult.scope,
      platformRoles: authResult.platformRoles,
      ...(activeDomainEntityId ? { activeDomainEntityId } : {}),
    },
    effectivePermissionKeys: normalizedPermissionKeys,
    permissionVersion: authResult.permissionVersion,
    tokenExpiry,
  };

  localStorage.setItem("auth_session_v1", JSON.stringify(session));

  dispatch(setPermissions(normalizedPermissionKeys));
  dispatch(setUser(session.user));
  dispatch(clearPendingBootstrap());
  dispatch(clearOtp());
}

/**
 * Send OTP mutation
 */
export function useSendOtp() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: (phone: string) => authService.sendOtp(phone),
    onSuccess: (_response, phone) => {
      toast.success("OTP sent successfully");

      // Update UI state to show OTP input
      dispatch(setOtpSentTo(phone));

      // Invalidate any auth-related queries
      queryClient.invalidateQueries({ queryKey: QueryKeys.auth.all });
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, "Failed to send OTP"));
    },
  });
}

/**
 * Everything that happens after a credential is accepted, regardless of which credential it was.
 *
 * OTP and email/password both end at the same bootstrap payload, so they must both continue through
 * this one path: auto-activate when there is exactly one destination, activate a platform session
 * when there is none, otherwise hand the choice to the tenant/role picker. Duplicating it per method
 * is how the two flows would quietly drift apart.
 */
async function completeBootstrap(
  dispatch: ReturnType<typeof useDispatch>,
  queryClient: ReturnType<typeof useQueryClient>,
  bootstrapResult: authService.VerifyOtpResponse,
): Promise<void> {
  const availableTenants = Array.isArray(bootstrapResult.availableTenants)
    ? bootstrapResult.availableTenants
    : [];
  const availableContexts = Array.isArray(bootstrapResult.availableContexts)
    ? bootstrapResult.availableContexts
    : [];

  if (availableTenants.length === 0 && availableContexts.length === 0) {
    // No tenant contexts at all — only issued for accounts holding a platform role (see backend
    // VerifyOtpHandler). Activate a PLATFORM-scope session directly instead of showing a picker.
    try {
      const deviceId = authService.getDeviceIdentifier();
      const authResult = await authService.activateSession(
        bootstrapResult.bootstrapToken,
        null,
        "",
        null,
        deviceId,
        "Web",
      );

      persistAuthenticatedSession(dispatch, authResult);
      toast.success("Login successful");
      queryClient.invalidateQueries({ queryKey: QueryKeys.auth.all });
    } catch (error: unknown) {
      toast.error(
        getErrorMessage(error, "This account has no accessible tenant or platform role."),
      );
    }
    return;
  }

  if (availableTenants.length === 1 && availableContexts.length === 1) {
    const selectedContext = availableContexts[0];
    const deviceId = authService.getDeviceIdentifier();
    const authResult = await authService.activateSession(
      bootstrapResult.bootstrapToken,
      selectedContext.tenantId,
      selectedContext.contextType,
      selectedContext.domainEntityId ?? null,
      deviceId,
      "Web",
    );

    if (authResult.tenantId) {
      clearStoredAcademicYearSelection(authResult.tenantId);
    }

    persistAuthenticatedSession(
      dispatch,
      authResult,
      selectedContext.domainEntityId ?? null,
    );
    toast.success("Login successful");
    queryClient.invalidateQueries({ queryKey: QueryKeys.auth.all });
    return;
  }

  dispatch(
    setPendingBootstrap({
      userId: bootstrapResult.userId,
      userName: bootstrapResult.userName,
      email: bootstrapResult.email,
      phoneNumber: bootstrapResult.phoneNumber,
      bootstrapToken: bootstrapResult.bootstrapToken,
      expiresAt: authService.getTokenExpiration(bootstrapResult.expiresIn),
      availableTenants,
      availableContexts,
    }),
  );
  toast.success("Choose a tenant and role to continue");
}

/**
 * Verify OTP mutation
 */
export function useVerifyOtp() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({ phoneNumber, otpCode }: { phoneNumber: string; otpCode: string }) => {
      const deviceId = authService.getDeviceIdentifier();
      return authService.verifyOtp(phoneNumber, otpCode, deviceId, "Web");
    },
    onSuccess: (bootstrapResult) =>
      completeBootstrap(dispatch, queryClient, bootstrapResult),
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, "Failed to verify OTP"));
    },
  });
}

/**
 * Redeem the one-time code Google sign-in returns with.
 *
 * Third entry point into the same completeBootstrap path — OTP, password and Google differ only in
 * how the user proved who they are.
 */
export function useExchangeGoogleCode() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: (code: string) => authService.exchangeGoogleCode(code),
    onSuccess: (bootstrapResult) =>
      completeBootstrap(dispatch, queryClient, bootstrapResult),
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, "Google sign-in could not be completed"));
    },
  });
}

/**
 * Verify an email/password credential.
 *
 * Converges on the same completeBootstrap path as OTP, so tenant selection, activation and session
 * persistence are identical — there is one session implementation, not one per sign-in method.
 */
export function useVerifyPassword() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) => {
      const deviceId = authService.getDeviceIdentifier();
      return authService.verifyPassword(email, password, deviceId, "Web");
    },
    onSuccess: (bootstrapResult) =>
      completeBootstrap(dispatch, queryClient, bootstrapResult),
    onError: (error: unknown) => {
      // The server answers every rejection identically on purpose; surface its message rather than
      // guessing at a more specific one.
      toast.error(getErrorMessage(error, "Invalid email address or password"));
    },
  });
}

export function useActivateSession() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({
      bootstrapToken,
      tenantId,
      activeContext,
      activeDomainEntityId,
    }: {
      bootstrapToken: string;
      tenantId: string;
      activeContext: string;
      activeDomainEntityId?: string | null;
    }) => {
      const deviceId = authService.getDeviceIdentifier();
      return authService.activateSession(
        bootstrapToken,
        tenantId,
        activeContext,
        activeDomainEntityId ?? null,
        deviceId,
        "Web",
      );
    },
    onSuccess: (authResult, variables) => {
      if (authResult.tenantId) {
        clearStoredAcademicYearSelection(authResult.tenantId);
      }

      persistAuthenticatedSession(
        dispatch,
        authResult,
        variables.activeDomainEntityId ?? null,
      );
      toast.success("Login successful");
      queryClient.invalidateQueries({ queryKey: QueryKeys.auth.all });
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, "Failed to activate session"));
    },
  });
}

/**
 * Refresh token mutation
 */
export function useRefreshToken() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      refreshToken,
      deviceIdentifier,
      userId,
    }: {
      refreshToken: string;
      deviceIdentifier: string;
      userId: string;
    }) => authService.refreshToken(refreshToken, deviceIdentifier, userId),
    onSuccess: () => {
      toast.success("Token refreshed successfully");
      // Invalidate auth queries
      queryClient.invalidateQueries({ queryKey: QueryKeys.auth.all });
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, "Failed to refresh token"));
    },
  });
}
